// src/components/email/EmailComposer.jsx
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { 
  FiSend, FiSave, FiX, FiPaperclip, FiEye, FiUsers, FiUploadCloud, 
  FiPlus, FiTrash2, FiChevronDown, FiFile, FiImage, FiInfo, FiEdit,
  FiZap, FiClock, FiCheck, FiAlertCircle, FiRefreshCw, FiMonitor, FiSmartphone
} from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import { emailService } from '../../utils/emailUtils';
import contactsService from '../../utils/contactsUtils';

const EmailComposer = ({ templateId, emailData, onClose, mode = 'compose', onSent, isFullPage = false }) => {
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  const [emailFormData, setEmailFormData] = useState({
    to: emailData?.to || [{ email: '', name: '' }],
    cc: emailData?.cc || [],
    bcc: emailData?.bcc || [],
    replyTo: emailData?.replyTo || '',
    subject: emailData?.subject || '',
    htmlContent: emailData?.htmlContent || '',
    attachments: emailData?.attachments || [],
    templateId: templateId || emailData?.templateId || null,
    scheduledAt: emailData?.scheduledAt || null,
    originalEmailId: emailData?.originalEmailId || null
  });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [scheduledSend, setScheduledSend] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [csvImportStatus, setCsvImportStatus] = useState(null);
  const [showCsvHelp, setShowCsvHelp] = useState(false);
  const csvInputRef = useRef(null);
  
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showAiContentPanel, setShowAiContentPanel] = useState(false);
  const [aiContentPrompt, setAiContentPrompt] = useState('');
  const [aiContentLoading, setAiContentLoading] = useState(false);
  const [showAiDesignPanel, setShowAiDesignPanel] = useState(false);
  const [aiDesignPrompt, setAiDesignPrompt] = useState('');
  const [aiDesignLoading, setAiDesignLoading] = useState(false);
  const [designOptions, setDesignOptions] = useState({
    includeHeader: true,
    includeFooter: true,
    colorScheme: 'professional',
    tone: 'professional'
  });

  useEffect(() => {
    // Check for contacts in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const contactIds = urlParams.get('contacts');
    
    if (contactIds && organizationId) {
      loadContactsFromIds(contactIds.split(','));
    }
  }, [organizationId]);

  const loadContactsFromIds = async (contactIds) => {
    try {
      const contacts = await Promise.all(
        contactIds.map(id => contactsService.getContact(organizationId, id))
      );
      
      const recipients = contacts
        .filter(contact => contact.email)
        .map(contact => ({
          email: contact.email,
          name: contact.name
        }));
      
      setEmailFormData(prev => ({
        ...prev,
        to: recipients
      }));
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  // Email-safe HTML sanitization and processing
  const sanitizeAndProcessHtml = (html) => {
    if (!html) return '';
    
    // Create a temporary div to manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Convert any relative URLs to absolute
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      if (img.src && !img.src.startsWith('http')) {
        // Handle relative URLs - in a real app, you'd convert these properly
        console.warn('Relative image URL detected:', img.src);
      }
      // Add email-safe image attributes
      if (!img.style.maxWidth) {
        img.style.maxWidth = '100%';
      }
      if (!img.style.height) {
        img.style.height = 'auto';
      }
      if (!img.getAttribute('border')) {
        img.setAttribute('border', '0');
      }
    });
    
    // Process links to be email-safe
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      if (!link.style.color) {
        link.style.color = '#2563eb';
      }
      if (!link.style.textDecoration) {
        link.style.textDecoration = 'underline';
      }
    });
    
    // Ensure paragraphs have proper spacing
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (!p.style.margin) {
        p.style.margin = '0 0 16px 0';
      }
    });
    
    // Process lists for better email compatibility
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      if (!list.style.paddingLeft) {
        list.style.paddingLeft = '30px';
      }
      if (!list.style.margin) {
        list.style.margin = '16px 0';
      }
    });
    
    return tempDiv.innerHTML;
  };

  // Generate email-safe HTML with proper structure
  const generateEmailSafeHtml = (content) => {
    const emailSafeStyles = `
      body { 
        font-family: Arial, sans-serif; 
        line-height: 1.6; 
        color: #333333; 
        margin: 0; 
        padding: 0; 
        -webkit-text-size-adjust: 100%; 
        -ms-text-size-adjust: 100%; 
      }
      table { 
        border-collapse: collapse; 
        mso-table-lspace: 0pt; 
        mso-table-rspace: 0pt; 
      }
      img { 
        border: 0; 
        height: auto; 
        line-height: 100%; 
        outline: none; 
        text-decoration: none; 
        -ms-interpolation-mode: bicubic; 
      }
      .email-container { 
        max-width: 600px; 
        margin: 0 auto; 
        background-color: #ffffff; 
      }
      .email-content { 
        padding: 20px; 
      }
      @media only screen and (max-width: 620px) {
        .email-container { 
          width: 95% !important; 
          max-width: 95% !important; 
        }
        .email-content { 
          padding: 15px !important; 
        }
      }
    `;

    return `
      <!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
      <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="x-apple-disable-message-reformatting" />
        <!--[if !mso]><!-->
        <meta http-equiv="X-UA-Compatible" content="IE=edge" />
        <!--<![endif]-->
        <title>${emailFormData.subject || 'Email'}</title>
        <style type="text/css">
          ${emailSafeStyles}
        </style>
        <!--[if gte mso 9]>
        <xml>
          <o:OfficeDocumentSettings>
            <o:AllowPNG/>
            <o:PixelsPerInch>96</o:PixelsPerInch>
          </o:OfficeDocumentSettings>
        </xml>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #f4f4f4;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f4f4f4;">
          <tr>
            <td align="center" style="padding: 20px 0;">
              <table role="presentation" class="email-container" cellspacing="0" cellpadding="0" border="0" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                <tr>
                  <td class="email-content" style="padding: 20px;">
                    ${sanitizeAndProcessHtml(content)}
                  </td>
                </tr>
                <tr>
                  <td style="padding: 20px; background-color: #f8f9fa; border-top: 1px solid #dee2e6; text-align: center; font-size: 12px; color: #6c757d;">
                    <p style="margin: 0;">
                      This email was sent from ${userProfile?.organizationName || 'Your Organization'}
                      ${userProfile?.email ? ` | <a href="mailto:${userProfile.email}" style="color: #6c757d;">${userProfile.email}</a>` : ''}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  };

  // Get preview HTML with proper email structure
  const getPreviewHtml = () => {
    if (!emailFormData.htmlContent) return '<p style="color: #666; text-align: center; padding: 40px;">No content to preview</p>';
    
    let previewContent = emailFormData.htmlContent;
    
    // Replace common variables with sample data
    const replacements = {
      '{{organization.name}}': userProfile?.organizationName || 'Your Organization',
      '{{organization.email}}': userProfile?.email || 'contact@yourorganization.com',
      '{{organization.phone}}': userProfile?.phone || '+1 (555) 123-4567',
      '{{organization.address}}': '123 Business Street, City, State 12345',
      '{{content}}': '<p style="margin: 0 0 16px 0; line-height: 1.6;">This is sample content that would be inserted into the email template.</p>',
      '{{customer.name}}': 'John Smith',
      '{{customer.email}}': 'john.smith@example.com',
      '{{order.number}}': 'ORD-2025-1234',
      '{{invoice.number}}': 'INV-2025-5678',
      '{{invoice.amount}}': '$150.00',
      '{{invoice.dueDate}}': 'May 25, 2025',
      '{{tracking.number}}': 'TRK123456789'
    };
    
    Object.entries(replacements).forEach(([placeholder, value]) => {
      previewContent = previewContent.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    });
    
    return previewContent;
  };

  // Extract plain text from HTML for email fallback
  const extractTextFromHTML = (html) => {
    if (!html) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizeAndProcessHtml(html);
    
    // Add line breaks for better text formatting
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      p.innerHTML = p.innerHTML + '\n\n';
    });
    
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(h => {
      h.innerHTML = h.innerHTML + '\n\n';
    });
    
    const lists = tempDiv.querySelectorAll('li');
    lists.forEach(li => {
      li.innerHTML = '• ' + li.innerHTML + '\n';
    });
    
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Enhanced send function with proper HTML processing
  const handleSend = async () => {
    if (!validateForm()) return;

    // Add Reply-To validation
    if (emailFormData.replyTo && !validateSingleEmail(emailFormData.replyTo)) {
      setError('Please enter a valid Reply-To email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Process HTML content for email safety
      const processedHtmlContent = generateEmailSafeHtml(emailFormData.htmlContent);
      const textContent = extractTextFromHTML(emailFormData.htmlContent);

      const emailPayload = {
        to: emailFormData.to.filter(r => r.email && validateEmail(r.email)),
        cc: emailFormData.cc.filter(r => r.email && validateEmail(r.email)),
        bcc: emailFormData.bcc.filter(r => r.email && validateEmail(r.email)),
        replyTo: emailFormData.replyTo || undefined,
        subject: emailFormData.subject,
        htmlBody: processedHtmlContent,
        textBody: textContent,
        attachments: emailFormData.attachments.map(att => ({
          name: att.name,
          content: att.content,
          mimeType: att.type
        })),
        module: 'email-composer',
        organizationId: organizationId,
        entityId: emailFormData.templateId || null,
        templateId: emailFormData.templateId,
        scheduledAt: emailFormData.scheduledAt,
        trackOpens: true,
        trackClicks: true
      };

      const result = await emailService.sendEmail(emailPayload);
      
      if (result.success) {
        setSuccess(true);
        if (onSent) onSent(result);
        
        // Clear form after successful send
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  // Rest of the component methods remain the same...
  const getModeTitle = () => {
    switch (mode) {
      case 'forward': return 'Forward Message';
      case 'edit': return 'Edit & Resend Message';
      default: return 'New Message';
    }
  };

  const getModeIcon = () => {
    switch (mode) {
      case 'forward': return <FiSend className="mr-2" size={20} />;
      case 'edit': return <FiEdit className="mr-2" size={20} />;
      default: return <FiSend className="mr-2" size={20} />;
    }
  };

  // Load templates on component mount
  useEffect(() => {
    if (organizationId) {
      loadTemplates();
    }
  }, [organizationId]);

  const handleAIContentGeneration = async () => {
    if (!aiContentPrompt.trim()) {
      setError('Please enter a prompt for AI content generation');
      return;
    }

    try {
      setAiContentLoading(true);
      setError(null);
      
      const result = await emailService.generateAIContent(aiContentPrompt, {
        useCase: 'email-content',
        tone: 'professional',
        communicationType: 'email',
        subject: emailFormData.subject,
        recipients: emailFormData.to.map(r => r.email).join(', '),
        industry: userProfile?.industry,
        purpose: 'business_communication'
      });
      
      if (result.success) {
        const htmlContent = result.htmlContent || emailService.convertMarkdownToHTML(result.result);
        
        setEmailFormData(prev => ({
          ...prev,
          htmlContent: htmlContent
        }));
        setShowAiContentPanel(false);
        setAiContentPrompt('');
      } else {
        throw new Error(result.error || 'AI generation failed');
      }
    } catch (err) {
      setError(`AI Generation Error: ${err.message}`);
      console.error('AI generation error:', err);
    } finally {
      setAiContentLoading(false);
    }
  };

  const handleAIDesignGeneration = async () => {
    if (!aiDesignPrompt.trim()) {
      setError('Please enter a prompt for AI design generation');
      return;
    }

    try {
      setAiDesignLoading(true);
      setError(null);
      
      const organizationInfo = {
        name: userProfile?.organizationName || 'Your Organization',
        primaryColor: userProfile?.primaryColor || '#2563eb',
        email: userProfile?.email
      };

      const result = await emailService.generateEmailTemplate(
        aiDesignPrompt,
        'custom',
        organizationInfo,
        {
          includeHeader: designOptions.includeHeader,
          includeFooter: designOptions.includeFooter,
          colorScheme: designOptions.colorScheme,
          tone: designOptions.tone
        }
      );
      
      if (result.success) {
        setEmailFormData(prev => ({
          ...prev,
          htmlContent: result.htmlContent
        }));
        setShowAiDesignPanel(false);
        setAiDesignPrompt('');
      } else {
        throw new Error(result.error || 'AI design generation failed');
      }
    } catch (err) {
      setError(`AI Design Error: ${err.message}`);
      console.error('AI design generation error:', err);
    } finally {
      setAiDesignLoading(false);
    }
  };

  // Load templates and other functions remain the same...
  const loadTemplates = async () => {
    try {
      const moduleTypes = [
        'general', 'customer', 'order', 'billing', 
        'newsletter', 'support', 'marketing', 'notification'
      ];
      
      const loadedTemplates = [];
      for (const moduleType of moduleTypes) {
        try {
          const templateData = await emailService.getEmailTemplate(organizationId, moduleType);
          if (templateData.template) {
            loadedTemplates.push({
              id: moduleType,
              name: templateData.template.name,
              subject: templateData.template.subject,
              htmlContent: templateData.template.htmlContent,
              module: moduleType
            });
          }
        } catch (err) {
          console.error(`Error loading ${moduleType} template:`, err);
        }
      }
      setTemplates(loadedTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        let htmlContent = template.htmlContent;
        
        // Replace common variables with user/organization info
        htmlContent = htmlContent.replace(/{{organization\.name}}/g, userProfile?.organizationName || 'Your Organization');
        htmlContent = htmlContent.replace(/{{organization\.email}}/g, userProfile?.email || '');
        
        setEmailFormData(prev => ({
          ...prev,
          subject: template.subject,
          htmlContent: htmlContent,
          templateId: templateId
        }));
      }
    } catch (err) {
      setError('Failed to load template');
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setCsvImportStatus(null);
      
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      
      const rows = text.split(/\r?\n/).filter(row => row.trim());
      
      let validCount = 0;
      let invalidCount = 0;
      let duplicateCount = 0;
      const newRecipients = [];
      const existingEmails = new Set(emailFormData.to.map(r => r.email.toLowerCase()));
      
      for (const row of rows) {
        const columns = row.split(',').map(col => col.trim());
        
        if (columns.length >= 1) {
          const email = columns[0];
          const name = columns.length >= 2 ? columns[1] : '';
          
          if (validateEmail(email)) {
            if (!existingEmails.has(email.toLowerCase())) {
              newRecipients.push({ email, name });
              existingEmails.add(email.toLowerCase());
              validCount++;
            } else {
              duplicateCount++;
            }
          } else {
            invalidCount++;
          }
        }
      }
      
      if (newRecipients.length > 0) {
        setEmailFormData(prev => ({
          ...prev,
          to: [...prev.to, ...newRecipients]
        }));
      }
      
      setCsvImportStatus({
        total: rows.length,
        valid: validCount,
        invalid: invalidCount,
        duplicate: duplicateCount
      });
      
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    } catch (err) {
      setError('Failed to import CSV: ' + err.message);
    }
  };

  const handleRecipientChange = (index, field, value, type = 'to') => {
    setEmailFormData(prev => ({
      ...prev,
      [type]: prev[type].map((recipient, i) => 
        i === index ? { ...recipient, [field]: value } : recipient
      )
    }));
  };

  const addRecipient = (type = 'to') => {
    setEmailFormData(prev => ({
      ...prev,
      [type]: [...prev[type], { email: '', name: '' }]
    }));
  };

  const removeRecipient = (index, type = 'to') => {
    setEmailFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleAttachmentAdd = async (files) => {
    try {
      const newAttachments = [];
      
      for (const file of files) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newAttachments.push({
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          content: base64
        });
      }

      setEmailFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments]
      }));
    } catch (err) {
      setError('Failed to add attachments');
    }
  };

  const removeAttachment = (attachmentId) => {
    setEmailFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(att => att.id !== attachmentId)
    }));
  };

  const validateSingleEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = () => {
    if (!emailFormData.to.some(recipient => recipient.email && validateEmail(recipient.email))) {
      setError('Please enter at least one valid recipient email');
      return false;
    }

    if (!emailFormData.subject.trim()) {
      setError('Please enter a subject');
      return false;
    }

    if (!emailFormData.htmlContent.trim()) {
      setError('Please enter email content');
      return false;
    }

    return true;
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);
      const draftData = {
        ...emailFormData,
        savedAt: new Date().toISOString(),
        isDraft: true
      };
      
      localStorage.setItem(`email-draft-${Date.now()}`, JSON.stringify(draftData));
      setSaveAsDraft(true);
      
      setTimeout(() => setSaveAsDraft(false), 2000);
    } catch (err) {
      setError('Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // Updated renderPreview function in EmailComposer
  const renderPreview = () => {
    const previewHtml = getPreviewHtml();
    
    return (
      <div className="h-full p-6">
        <div className="flex justify-center mb-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setPreviewDevice('desktop')}
              className={`px-3 py-1 rounded text-sm flex items-center ${
                previewDevice === 'desktop' ? 'bg-white shadow' : ''
              }`}
            >
              <FiMonitor className="mr-1" size={14} />
              Desktop
            </button>
            <button
              onClick={() => setPreviewDevice('mobile')}
              className={`px-3 py-1 rounded text-sm flex items-center ${
                previewDevice === 'mobile' ? 'bg-white shadow' : ''
              }`}
            >
              <FiSmartphone className="mr-1" size={14} />
              Mobile
            </button>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div 
            className={`border border-gray-200 rounded-lg overflow-hidden shadow-lg ${
              previewDevice === 'mobile' ? 'max-w-sm' : 'max-w-4xl w-full'
            }`}
            style={{ minHeight: '400px' }}
          >
            <div className="p-4 bg-gray-50 border-b text-sm text-gray-600">
              <div><strong>To:</strong> {emailFormData.to.map(r => r.email).join(', ')}</div>
              {emailFormData.cc.length > 0 && (
                <div><strong>CC:</strong> {emailFormData.cc.map(r => r.email).join(', ')}</div>
              )}
              {emailFormData.bcc.length > 0 && (
                <div><strong>BCC:</strong> {emailFormData.bcc.map(r => r.email).join(', ')}</div>
              )}
              {emailFormData.replyTo && (
                <div><strong>Reply-To:</strong> {emailFormData.replyTo}</div>
              )}
              <div><strong>Subject:</strong> {emailFormData.subject}</div>
            </div>
            <div className="overflow-auto" style={{ height: '600px' }}>
              {/* Use iframe with proper srcDoc for complete HTML documents */}
              <iframe
                srcDoc={previewHtml}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  backgroundColor: '#ffffff'
                }}
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Rest of the render methods stay largely the same but with updated preview
  const renderCsvImportStatus = () => {
    if (!csvImportStatus) return null;
    
    return (
      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
        <div className="font-medium text-blue-800 mb-1">CSV Import Results</div>
        <div className="grid grid-cols-2 gap-2">
          <div>Total rows: {csvImportStatus.total}</div>
          <div>Valid emails: <span className="text-green-600 font-medium">{csvImportStatus.valid}</span></div>
          <div>Invalid emails: <span className="text-red-600 font-medium">{csvImportStatus.invalid}</span></div>
          <div>Duplicates: <span className="text-yellow-600 font-medium">{csvImportStatus.duplicate}</span></div>
        </div>
      </div>
    );
  };

  const renderCsvHelpModal = () => {
    if (!showCsvHelp) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FiUploadCloud className="mr-2" size={18} />
            CSV Import Format
          </h3>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Your CSV file should follow this format:
            </p>
            
            <div className="bg-gray-50 p-3 rounded-md font-mono text-sm">
              email@example.com,John Doe<br/>
              another@example.com,Jane Smith<br/>
              third@example.com,Robert Johnson
            </div>
            
            <ul className="text-sm text-gray-600 space-y-2 list-disc pl-5">
              <li>First column: Email address (required)</li>
              <li>Second column: Recipient name (optional)</li>
              <li>No header row is needed</li>
              <li>Emails will be validated before adding</li>
              <li>Duplicate emails will be skipped</li>
            </ul>
            
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowCsvHelp(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAiContentPanel = () => {
    if (!showAiContentPanel) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Sparkles className="text-purple-600 mr-2" size={18} />
            AI Content Generator
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the email content you want to create:
              </label>
              <textarea
                value={aiContentPrompt}
                onChange={(e) => setAiContentPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={5}
                placeholder="E.g., Write a professional follow-up email to a client who attended our product demo last week..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowAiContentPanel(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAIContentGeneration}
                disabled={aiContentLoading || !aiContentPrompt.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
              >
                {aiContentLoading ? (
                  <>
                    <FiRefreshCw className="animate-spin mr-2" size={14} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={14} />
                    Generate Content
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAiDesignPanel = () => {
    if (!showAiDesignPanel) return null;

    const colorSchemes = [
      { value: 'professional', label: 'Professional' },
      { value: 'modern', label: 'Modern' },
      { value: 'minimalist', label: 'Minimalist' },
      { value: 'vibrant', label: 'Vibrant' },
      { value: 'corporate', label: 'Corporate' }
    ];

    const tones = [
      { value: 'professional', label: 'Professional' },
      { value: 'friendly', label: 'Friendly' },
      { value: 'formal', label: 'Formal' },
      { value: 'casual', label: 'Casual' },
      { value: 'urgent', label: 'Urgent' }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Sparkles className="text-purple-600 mr-2" size={18} />
            AI Email Design Generator
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the email design you want to create:
              </label>
              <textarea
                value={aiDesignPrompt}
                onChange={(e) => setAiDesignPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                placeholder="E.g., Create a professional email template with a blue header, logo placement, and clean layout..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Scheme
                </label>
                <select
                  value={designOptions.colorScheme}
                  onChange={(e) => setDesignOptions({...designOptions, colorScheme: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {colorSchemes.map(scheme => (
                    <option key={scheme.value} value={scheme.value}>{scheme.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select
                  value={designOptions.tone}
                  onChange={(e) => setDesignOptions({...designOptions, tone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {tones.map(tone => (
                    <option key={tone.value} value={tone.value}>{tone.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={designOptions.includeHeader}
                  onChange={(e) => setDesignOptions({...designOptions, includeHeader: e.target.checked})}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Include Header</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={designOptions.includeFooter}
                  onChange={(e) => setDesignOptions({...designOptions, includeFooter: e.target.checked})}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Include Footer</span>
              </label>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowAiDesignPanel(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAIDesignGeneration}
                disabled={aiDesignLoading || !aiDesignPrompt.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
              >
                {aiDesignLoading ? (
                  <>
                    <FiRefreshCw className="animate-spin mr-2" size={14} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={14} />
                    Generate Design
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRecipientField = (recipients, type, label, show = true) => {
    if (!show) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center">
            <input
              type="file"
              ref={csvInputRef}
              onChange={handleCsvImport}
              accept=".csv"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => csvInputRef.current?.click()}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <FiUploadCloud size={14} className="mr-1" />
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setShowCsvHelp(true)}
              className="ml-1 text-gray-400 hover:text-gray-600"
              title="CSV Format Help"
            >
              <FiInfo size={14} />
            </button>
          </div>
        </div>
        
        {type === 'to' && renderCsvImportStatus()}
        
        {recipients.map((recipient, index) => (
          <div key={index} className="flex space-x-2">
            <div className="flex-1">
              <input
                type="email"
                placeholder="Email address"
                value={recipient.email}
                onChange={(e) => handleRecipientChange(index, 'email', e.target.value, type)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Name (optional)"
                value={recipient.name}
                onChange={(e) => handleRecipientChange(index, 'name', e.target.value, type)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            {recipients.length > 1 && (
              <button
                type="button"
                onClick={() => removeRecipient(index, type)}
                className="p-2 text-red-500 hover:text-red-700"
              >
                <FiTrash2 size={16} />
              </button>
            )}
          </div>
        ))}
        
        <button
          type="button"
          onClick={() => addRecipient(type)}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          <FiPlus size={14} className="mr-1" />
          Add {label.toLowerCase()}
        </button>
      </div>
    );
  };

  const renderReplyToField = () => {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Reply-To (Optional)
        </label>
        <div className="flex space-x-2">
          <div className="flex-1">
            <input
              type="email"
              placeholder="reply@yourdomain.com"
              value={emailFormData.replyTo}
              onChange={(e) => setEmailFormData(prev => ({ ...prev, replyTo: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            {emailFormData.replyTo && !validateSingleEmail(emailFormData.replyTo) && (
              <p className="mt-1 text-xs text-red-600">Please enter a valid email address</p>
            )}
          </div>
          {emailFormData.replyTo && (
            <button
              type="button"
              onClick={() => setEmailFormData(prev => ({ ...prev, replyTo: '' }))}
              className="p-2 text-gray-400 hover:text-red-600"
              title="Clear Reply-To"
            >
              <FiX size={16} />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Replies to this email will be sent to this address instead of the sender's address
        </p>
      </div>
    );
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCheck className="text-green-600" size={32} />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Email Sent Successfully!</h3>
            <p className="text-gray-600 mb-6">Your email has been sent to all recipients.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
          <FiAlertCircle className="mr-2" size={16} />
          {error}
        </div>
      )}

      <div className="flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-semibold flex items-center">
            {getModeIcon()}
            {getModeTitle()}
            {mode === 'forward' && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
                Forwarding
              </span>
            )}
            {mode === 'edit' && (
              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-600 rounded-full">
                Editing
              </span>
            )}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`p-2 rounded-lg ${previewMode ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            >
              <FiEye size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {previewMode ? (
            renderPreview()
          ) : (
            <div className="p-6 space-y-6">
              {/* Template Selection */}
              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Use Template (Optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {templates.map(template => (
                      <div 
                        key={template.id} 
                        onClick={() => loadTemplate(template.id)}
                        className={`border rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors ${
                          emailFormData.templateId === template.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="font-medium mb-2">{template.name}</div>
                        <div className="text-sm text-gray-600 truncate">{template.subject}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div className="space-y-4">
                {renderRecipientField(emailFormData.to, 'to', 'To')}
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowCc(!showCc)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showCc ? 'Hide CC' : 'Add CC'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBcc(!showBcc)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {showBcc ? 'Hide BCC' : 'Add BCC'}
                  </button>
                </div>

                {renderRecipientField(emailFormData.cc, 'cc', 'CC', showCc)}
                {renderRecipientField(emailFormData.bcc, 'bcc', 'BCC', showBcc)}
              </div>

              {/* Reply-To Field */}
              {renderReplyToField()}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailFormData.subject}
                  onChange={(e) => setEmailFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Email subject..."
                />
              </div>

              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Attachments
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <FiPaperclip className="mr-1" size={14} />
                    Add Files
                  </button>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleAttachmentAdd(Array.from(e.target.files))}
                  multiple
                  className="hidden"
                />

                {emailFormData.attachments.length > 0 && (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {emailFormData.attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center">
                          <FiFile size={16} className="text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium">{attachment.name}</div>
                            <div className="text-xs text-gray-500">
                              {(attachment.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeAttachment(attachment.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex mt-2 space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAiContentPanel(true)}
                  className="px-3 py-1.5 text-sm bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-md hover:from-purple-700 hover:to-blue-700 flex items-center"
                >
                  <Sparkles className="mr-1" size={14} />
                  Generate Content
                </button>
                <button
                  type="button"
                  onClick={() => setShowAiDesignPanel(true)}
                  className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 flex items-center"
                >
                  <FiZap className="mr-1" size={14} />
                  Design Email
                </button>
              </div>

              {/* Email Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                <div className="border border-gray-300 rounded-md">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={emailFormData.htmlContent}
                    onChange={(content) => setEmailFormData(prev => ({ ...prev, htmlContent: content }))}
                    modules={quillModules}
                    style={{ height: '300px' }}
                  />
                </div>
              </div>

              {/* Scheduling Options */}
              <div className="mt-16 p-4 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={scheduledSend}
                      onChange={(e) => setScheduledSend(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Schedule send</span>
                  </label>
                  
                  {scheduledSend && (
                    <input
                      type="datetime-local"
                      value={emailFormData.scheduledAt || ''}
                      onChange={(e) => setEmailFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveDraft}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
            >
              <FiSave className="mr-2" size={16} />
              {saveAsDraft ? 'Saved!' : 'Save Draft'}
            </button>
            
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
            >
              <FiEye className="mr-2" size={16} />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSend}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FiSend className="mr-2" size={16} />
                  {scheduledSend ? 'Schedule Send' : 'Send Email'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      {renderCsvHelpModal()}
      {renderAiContentPanel()}
      {renderAiDesignPanel()}
    </div>
  );
};

export default EmailComposer;